import { logger } from '../../lib/logging'
import { NoTeamAvailableError } from '../../../shared/errors/NoTeamAvailableError'

export class Authentication {
  constructor (app) {
    this.app = app
  }

  init () {
    throw new Error('Init has not been implemented.')
  }

  login () {
    throw new Error('Login has not been implemented.')
  }

  logout () {
    throw new Error('Logout has not been implemented.')
  }

  refresh () {
    throw new Error('Refresh has not been implemented.')
  }

  async afterLogin (user, teamId, isSignupFlow = false) {
    await this.createUserHistory({ userId: user.id })

    try {
      return {
        user,
        ...await this.getTeamsData(user, teamId, isSignupFlow)
      }
    } catch (error) {
      if (error.name === 'UserTeamNotFound') {
        logger.warn(error)
        throw new NoTeamAvailableError()
      } else {
        throw error
      }
    }
  }

  /**
   * Returns if the page is only available for users that are not authenticated.
   * @param {string} path
   * @returns {boolean}
   */
  isGuestPage (path) {
    // Remove leading slash as this will be included when getting location.pathname
    path = path.replace(/^\//, '')

    return this.guestPages().some(page => path.startsWith(page))
  }

  /**
   * Returns if the page is available for both authenticated and non-authenticated users.
   *
   * @param {string} path
   * @returns {boolean}
   */
  isPublicPage (path) {
    // Remove leading slash as this will be included when getting location.pathname
    path = path.replace(/^\//, '')

    return this.publicPages().some(page => {
      page = this.app.getAppUrl(page)
      page = page.replace(/^\//, '') // Remove leading slash
      return path.startsWith(page)
    })
  }

  /**
   * Returns if the page is only available for authenticated users.
   * @param {string} path
   * @returns {boolean}
   */
  isAuthenticatedPage (path) {
    return !this.isGuestPage(path) && !this.isPublicPage(path)
  }

  /**
   * Returns a list of pages that you can only visit if you are not authenticated.
   *
   * Note: `login` is not a guest page since it contains the team picker for authenticated users.
   */
  guestPages () {
    return [
      'reset',
      'signup',
      'forgot-password'
    ]
  }

  /**
   * Pages that are accessible for both authenticated and non-authenticated users.
   */
  publicPages () {
    return [
      'login',
      'confirm',
      'emailconfirm',
      'token'
    ]
  }

  /**
   * Create user history.
   *
   * @param {object} values
   */
  async createUserHistory ({ userId }) {
    const result = await this.app.service('/api/user/history').create({ userId })
    this.app.set('appUserHistoryId', result.id)
    return result
  }

  /**
   * Gets teams data for the logged in user
   *
   * @param {object} user
   * @param string teamId
   * @param bool isSignupFlow
   */
  async getTeamsData (user, teamId, isSignupFlow = false) {
    this.app._user = user
    const teamContext = this.app.teamContext

    const { availableTeams, activeTeam, activeUserTeam, teamsUserIsInvitedTo } = await teamContext.getTeamContext(user, teamId)

    /*
       when login request for an existing user is comming from signup flow, we should check
       both availableTeams and teamsUserIsInvitedTo for throwing NoTeamAvailableError error.
       The reason is that, in availableTeams user.status is 'active' and
       team.archived is 'false'. it doesn't take into account teams that user.status is 'invited' and
       team.archived is 'false' (Which is the case in inviting a user).
       Therefore, the NoTeamAvailableError is returned and signup flow is halted.
       for more information take look at this ticket: https://rodeodev.atlassian.net/browse/SUP-1280
      */

    if ((!isSignupFlow && availableTeams.length === 0) ||
         (isSignupFlow && availableTeams.length === 0 && teamsUserIsInvitedTo.length === 0)) {
      throw new NoTeamAvailableError()
    }

    teamContext.setTeams(availableTeams)

    if (activeTeam && activeUserTeam) {
      teamContext.setActiveTeam(activeTeam)
      teamContext.setActiveUserTeam(activeUserTeam)
    } else if (availableTeams.length === 1) {
      // If the user only has one team, we'll automatically switch to that team.
      const teamId = availableTeams[0].id
      const switchResult = await teamContext.switchToTeamById({ userId: user.id, teamId })
      return {
        activeTeam: switchResult.activeTeam,
        availableTeams,
        activeUserTeam: switchResult.activeUserTeam,
        teamsUserIsInvitedTo
      }
    }

    return {
      activeTeam,
      availableTeams,
      activeUserTeam,
      teamsUserIsInvitedTo
    }
  }
}
